/**
 * The bare domain: "Jupiter, by Moyae". A landing page only; no sign-in here. Each practice signs in at its own
 * address ({tenant}.pacs.<domain>). Eye images are from Wikimedia Commons under the licenses credited in the footer.
 */
import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { JupiterCanvas } from "./JupiterCanvas";

const font = `"Helvetica Neue", Helvetica, Arial, system-ui, sans-serif`;
const caps: CSSProperties = { textTransform: "uppercase", letterSpacing: "0.22em", fontFamily: font };
const gutter = "clamp(16px, 6vw, 96px)";

const CREDITS = [
  { file: "/landing/oct-angiography.jpg", title: "OCT angiography of the foveal avascular zone (cropped, outline and labels removed)", author: "Werner JU, Böhm F, Lang GE, Dreyhaupt J, Lang GK, Enders C", license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0", page: "https://commons.wikimedia.org/wiki/File:Fondo_de_ojo_Ostrucci%C3%B3n_Vena_F%C3%B3vea_avascular.png" },
  { file: "/landing/macular-oct.jpg", title: "Macular OCT depicting Central Serous Chorioretinopathy in the Left Eye", author: "Ashley Ireland, OD", license: "CC BY-SA 3.0", licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0", page: "https://commons.wikimedia.org/wiki/File:Macular_OCT_depicting_Central_Serous_Chorioretinopathy_in_the_Left_Eye.png" },
  { file: "/landing/slit-lamp-eye-exam.jpg", title: "Slit lamp eye exam", author: "Mike Blyth", license: "CC BY-SA 3.0", licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0", page: "https://commons.wikimedia.org/wiki/File:Slit_lamp_eye_exam.jpg" },
  { file: "/landing/fundus-photograph-of-normal-left-eye.jpg", title: "Fundus photograph of normal left eye", author: "Mikael Häggström", license: "CC0", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/", page: "https://commons.wikimedia.org/wiki/File:Fundus_photograph_of_normal_left_eye.jpg" },
];

/** Fades a block in the first time it scrolls into view. */
function Reveal({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && (setShown(true), io.disconnect()), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(28px)", transition: `opacity 1.1s ease ${delay}s, transform 1.1s ease ${delay}s` }}>
      {children}
    </div>
  );
}

function Hero() {
  return (
    <section style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", padding: `0 ${gutter}` }}>
      <Reveal>
        <div style={{ ...caps, fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 18 }}>By Moyae</div>
        <h1 style={{ ...caps, margin: 0, fontWeight: 200, fontSize: "clamp(52px, 9vw, 128px)", letterSpacing: "0.32em", lineHeight: 1, color: "#fff" }}>Jupiter</h1>
        <p style={{ fontFamily: font, fontSize: "clamp(16px, 1.5vw, 20px)", color: "rgba(255,255,255,0.75)", marginTop: 24, maxWidth: 460, lineHeight: 1.5 }}>
          The secure imaging layer for the modern era.
        </p>
      </Reveal>
      <div style={{ position: "absolute", left: "50%", bottom: 28, transform: "translateX(-50%)", ...caps, fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Scroll</div>
    </section>
  );
}

function Feature({ overline, title, image, alt, flip, children }: { overline: string; title: string; image: string; alt: string; flip?: boolean; children: ReactNode }) {
  return (
    <section style={{ position: "relative", background: "linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.86) 30%, rgba(0,0,0,0.86) 70%, rgba(0,0,0,0) 100%)", padding: `clamp(80px, 14vh, 160px) ${gutter}` }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
          gap: "clamp(32px, 6vw, 96px)",
          alignItems: "center",
          maxWidth: 1280,
          margin: "0 auto",
        }}
      >
        <div style={{ order: flip ? 2 : 1 }}>
          <Reveal>
            <div style={{ ...caps, fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 16 }}>{overline}</div>
            <h2 style={{ ...caps, margin: 0, fontWeight: 400, fontSize: "clamp(26px, 3.2vw, 42px)", letterSpacing: "0.12em", lineHeight: 1.15, color: "#fff" }}>{title}</h2>
            <div style={{ fontFamily: font, fontSize: "clamp(15px, 1.25vw, 18px)", lineHeight: 1.7, color: "rgba(255,255,255,0.72)", marginTop: 22 }}>{children}</div>
          </Reveal>
        </div>
        <div style={{ order: flip ? 1 : 2 }}>
          <Reveal delay={0.15}>
            <figure style={{ margin: 0, position: "relative", aspectRatio: "4 / 3", overflow: "hidden", background: "#0a0a0a" }}>
              <img
                src={image}
                alt={alt}
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "saturate(0.82) contrast(1.05) brightness(0.92)" }}
              />
              <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 120px rgba(0,0,0,0.55)", pointerEvents: "none" }} />
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const P = ({ children }: { children: ReactNode }) => <p style={{ margin: "0 0 14px" }}>{children}</p>;

const MOTION_KEY = "jupiter-motion";

/** Motion is on by default on desktop, off on phones and for reduced-motion users; the visitor's choice is remembered. */
function initialMotion(): boolean {
  try {
    const saved = localStorage.getItem(MOTION_KEY);
    if (saved === "on" || saved === "off") return saved === "on";
  } catch {
    /* storage unavailable: fall through to the default */
  }
  const phone = window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return !phone && !reduce;
}

function MotionToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      aria-pressed={on}
      aria-label={on ? "Pause the animation" : "Play the animation"}
      style={{
        position: "fixed",
        right: "clamp(16px, 3vw, 32px)",
        bottom: "clamp(16px, 3vw, 32px)",
        zIndex: 4,
        ...caps,
        fontSize: 10,
        letterSpacing: "0.25em",
        color: "rgba(255,255,255,0.75)",
        background: "rgba(0,0,0,0.45)",
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: 999,
        padding: "9px 16px",
        cursor: "pointer",
        backdropFilter: "blur(6px)",
      }}
    >
      {on ? "Pause motion" : "Play motion"}
    </button>
  );
}

export default function Landing() {
  const [motion, setMotion] = useState(initialMotion);
  const toggleMotion = () =>
    setMotion((m) => {
      try {
        localStorage.setItem(MOTION_KEY, m ? "off" : "on");
      } catch {
        /* fine: this visit only */
      }
      return !m;
    });
  useEffect(() => {
    document.title = "Jupiter, by Moyae";
    document.body.style.background = "#000";
    return () => {
      document.body.style.background = "";
    };
  }, []);
  return (
    <div style={{ background: "#000", color: "#fff", minHeight: "100vh" }}>
      <JupiterCanvas animate={motion} />
      <MotionToggle on={motion} onToggle={toggleMotion} />
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: `26px ${gutter}`,
          background: "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0))",
        }}
      >
        <div style={{ ...caps, fontSize: 17, fontWeight: 500, letterSpacing: "0.45em" }}>Jupiter</div>
        <img src="/moyae-logo-white.svg" alt="Moyae" style={{ height: 20, width: "auto", opacity: 0.92 }} />
      </header>
      <main style={{ position: "relative", zIndex: 1 }}>
        <Hero />
        <Feature overline="The problem" title="PACS were built for another century." image="/landing/oct-angiography.jpg" alt="OCT angiography of the retinal capillary network">
          <P>Jupiter installs with one command and connects out on its own: no VPN, no static IPs, no tracing ports. Every instrument simply shows up.</P>
        </Feature>
        <Feature flip overline="Above everything" title="Jupiter sits on top of what you already own." image="/landing/macular-oct.jpg" alt="An OCT scan of the macula">
          <P>It doesn't replace your slit lamps, OCTs or archive. It ties them together: worklists go out from the schedule, captures come back tagged to the order that asked for them, from Haag-Streit, ZEISS or any DICOM instrument.</P>
          <P>One small appliance per site connects out to Jupiter. Nothing inbound, no VPN. Each instrument shows up the moment it asks for a worklist, and you approve it with a click.</P>
        </Feature>
        <Feature overline="Remote care" title="A slit lamp here. A specialist anywhere." image="/landing/slit-lamp-eye-exam.jpg" alt="An ophthalmologist examining a patient at a slit lamp">
          <P>The technician runs the lamp in a rural clinic and scans a code with their phone. The ophthalmologist joins from wherever they are and sees the lamp live, pinned beside the patient's worklist.</P>
          <P>Everyone on the call, the lamp's video and every voice, comes back to the chart as one record, ready for the note to write itself.</P>
        </Feature>
        <Feature flip overline="Any device, any system" title="Open it anywhere. Hand it off to where it belongs." image="/landing/fundus-photograph-of-normal-left-eye.jpg" alt="A fundus photograph of a normal retina">
          <P>View studies from any instrument on a laptop or a phone, without a workstation in the back room.</P>
          <P>When you need more, deep-link straight into the system that owns it: the EMR chart, a full DICOM viewer, the vendor's own review software. Results return to the EMR over FHIR, so nothing is left behind.</P>
        </Feature>
        <section style={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: `0 ${gutter}`, background: "linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.75) 100%)" }}>
          <Reveal>
            <div style={{ ...caps, fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 18 }}>Open standards</div>
            <h2 style={{ ...caps, margin: 0, fontWeight: 300, fontSize: "clamp(28px, 4vw, 54px)", letterSpacing: "0.18em", color: "#fff" }}>Your images stay yours.</h2>
            <div style={{ ...caps, fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 22, letterSpacing: "0.3em" }}>DICOM · DICOMweb · FHIR R4</div>
          </Reveal>
        </section>
      </main>
      <footer style={{ position: "relative", zIndex: 1, background: "#000", borderTop: "1px solid rgba(255,255,255,0.08)", padding: `40px ${gutter}`, fontFamily: font }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "space-between", alignItems: "center", ...caps, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
          <span>Jupiter by Moyae © {new Date().getFullYear()}</span>
          <span>Practices sign in at their own Jupiter address</span>
        </div>
        <div style={{ marginTop: 22, fontSize: 11, lineHeight: 1.7, color: "rgba(255,255,255,0.38)" }}>
          Images:{" "}
          {CREDITS.map((c, i) => (
            <span key={c.file}>
              <a href={c.page} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>{c.title}</a> by {c.author},{" "}
              <a href={c.licenseUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>{c.license}</a>
              {i < CREDITS.length - 1 ? "; " : "."}
            </span>
          ))}{" "}
          Resized and color-graded for this page. Jupiter is rendered in the browser.
        </div>
      </footer>
    </div>
  );
}
